import * as React from 'react';
import { FC } from 'react';

import { Icon } from '@rbilabs/components-library';
import { isUndefined } from 'lodash-es';
import { isNumber } from 'lodash/fp';
import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { CustomDisclaimer } from 'components/offers/ui-refresh/offer-disclaimer';
import { useForceStoreSelectionFlagged } from 'hooks/use-force-store-selection';
import { LoyaltyOffersCooldownDrawer } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown-drawer';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { FixableEvaluationErrors, IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';

import { DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE } from '../constants';
import { PointsNeededText } from '../incentive-card/incentive-card.styled';

import {
  POINTS_TRANSLATION_KEY,
  SHOULD_DISPLAY_ICON,
  SHOULD_USE_SECONDARY_VARIANT,
} from './constants';
import {
  BackgroundImage,
  ButtonContainer,
  ButtonsWrapper,
  DisclaimerText,
  ErrorMessage,
  ImageWrapper,
  Padding,
  RemoveRewardsButton,
  RewardDescription,
  RewardDetailsContainer,
  RewardName,
  StyledActionButton,
  StyledDisclaimerContainer,
  StyledIncentiveEvaluationMessages,
  StyledProgressMeter,
  StyledRewardImage,
  Wrapper,
} from './incentive-details.styled';
import { RewardPoints } from './reward-points';
import { IIncentiveDetailsViewProps } from './types';

export const IncentiveDetailsView: FC<IIncentiveDetailsViewProps> = ({
  name,
  image,
  description,
  moreInfo,
  isOffer,
  pointCost,
  locked,
  remainingPointsNeededForRedemption,
  redemptionPercentage,
  handleRedeemRewardClick,
  handleInRestaurantRedemptionClick,
  errorMessage,
  checkingAvailability,
  backgroundImage,
  canAddToInRestaurantOrder = false,
  canAddToMobileOrder = false,
  showRemoveRewardButton = false,
  handleRemoveRewardsClick,
  incentiveEvaluationResults,
  offersInCooldown = false,
}) => {
  const { formatMessage } = useIntl();
  useForceStoreSelectionFlagged();

  const enableMyCodeNewUI = Boolean(useFlag(LaunchDarklyFlag.ENABLE_MY_CODE_NEW_UI));
  const meterEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_METER_ON_REWARDS));

  const redeemText = !isNumber(pointCost)
    ? formatMessage({ id: 'addMobileOrder' })
    : `${formatMessage({ id: 'redeem' })} ${formatMessage({ id: 'reward' })}`;
  const inRestaurantRedemptionText = formatMessage({
    id: enableMyCodeNewUI ? 'redeemAtRestaurant' : 'redeemInRestaurant',
  });

  const hasServiceModeError = (
    evaluationResults: IIncentiveDetailsViewProps['incentiveEvaluationResults']
  ) =>
    !!evaluationResults?.some(
      evaluationResult =>
        evaluationResult.code === IncentiveEvaluationErrorCodes.INVALID_SERVICE_MODE
    );

  const hasBlockingErrors =
    !!errorMessage ||
    (offersInCooldown && isOffer) ||
    // Disable buttons if Feedback is not included in fixable incentive errors
    incentiveEvaluationResults?.some(
      evaluationResult =>
        !FixableEvaluationErrors.has(evaluationResult.code as IncentiveEvaluationErrorCodes)
    );

  const disableMobileOrderButton =
    hasBlockingErrors || hasServiceModeError(incentiveEvaluationResults);

  const disableInRestaurantOrderButton =
    hasBlockingErrors ||
    (DISABLE_IN_RESTAURANT_FOR_INVALID_SERVICE_MODE &&
      hasServiceModeError(incentiveEvaluationResults));

  return (
    <Wrapper>
      <ImageWrapper>
        {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
        {image && <StyledRewardImage alt={name} image={image} objectFitContain />}
      </ImageWrapper>
      <RewardDetailsContainer>
        {name && <RewardName>{name}</RewardName>}
        {description && <RewardDescription content={description} />}
        {!isUndefined(pointCost) && <RewardPoints pointCost={pointCost} locked={Boolean(locked)} />}
        {locked && (
          <>
            {meterEnabled && isNumber(redemptionPercentage) && (
              <StyledProgressMeter percentage={redemptionPercentage} />
            )}
            {isNumber(remainingPointsNeededForRedemption) && (
              <PointsNeededText>
                {formatMessage(
                  { id: 'yourXPointsAway' },
                  {
                    points: <span>{remainingPointsNeededForRedemption}</span>,
                    pointsSlang: POINTS_TRANSLATION_KEY,
                  }
                )}
              </PointsNeededText>
            )}
          </>
        )}
        {!isOffer && moreInfo && <DisclaimerText content={moreInfo} />}
        {/* padding element needed for safari / iOS native devices */}
        <Padding />
      </RewardDetailsContainer>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <ButtonsWrapper>
        {!locked && (
          <ButtonContainer>
            {canAddToMobileOrder && (
              <StyledActionButton
                disabled={disableMobileOrderButton}
                onClick={handleRedeemRewardClick}
                isLoading={checkingAvailability}
                startIcon={
                  SHOULD_DISPLAY_ICON ? (
                    <Icon icon={'mobile'} color={'white'} width="24px" aria-hidden />
                  ) : null
                }
              >
                {redeemText}
              </StyledActionButton>
            )}

            {canAddToInRestaurantOrder && (
              <StyledActionButton
                disabled={disableInRestaurantOrderButton}
                onClick={handleInRestaurantRedemptionClick}
                isLoading={checkingAvailability}
                startIcon={
                  SHOULD_DISPLAY_ICON ? (
                    <Icon icon={'storeTwo'} color={'primary'} width="24px" aria-hidden />
                  ) : null
                }
                variant={
                  SHOULD_USE_SECONDARY_VARIANT
                    ? ActionButtonVariants.OUTLINE
                    : ActionButtonVariants.PRIMARY
                }
              >
                {inRestaurantRedemptionText}
              </StyledActionButton>
            )}
          </ButtonContainer>
        )}

        {showRemoveRewardButton && (
          <ButtonContainer>
            <RemoveRewardsButton
              onClick={handleRemoveRewardsClick}
              variant={ActionButtonVariants.OUTLINE}
            >
              {formatMessage({ id: 'removeRewardsFromCart' })}
            </RemoveRewardsButton>
          </ButtonContainer>
        )}
      </ButtonsWrapper>

      {incentiveEvaluationResults && (
        <StyledIncentiveEvaluationMessages
          incentiveEvaluationResults={incentiveEvaluationResults}
          hideIcon
        />
      )}
      {isOffer && moreInfo && (
        <StyledDisclaimerContainer>
          <CustomDisclaimer content={moreInfo} />
        </StyledDisclaimerContainer>
      )}

      {offersInCooldown && <LoyaltyOffersCooldownDrawer />}
    </Wrapper>
  );
};
